import { useEffect, useState, type ReactElement } from 'react';

type DesktopPlatform = 'macos' | 'windows';

interface DesktopDownloadLink {
  readonly platform: DesktopPlatform;
  readonly url: string;
}

interface DesktopRelease {
  readonly version: string;
  readonly downloads: readonly DesktopDownloadLink[];
}

const UNAVAILABLE = '桌面版下載資訊暫時無法取得。';

const PLATFORM_LABELS: Record<DesktopPlatform, { readonly button: string; readonly kind: string }> = {
  macos: { button: '下載 macOS 版', kind: 'DMG' },
  windows: { button: '下載 Windows 版', kind: '安裝程式 .exe' },
};

function readDownloadUrl(download: unknown, expectedPathname: string): string {
  if (typeof download !== 'string') throw new Error(UNAVAILABLE);
  const url = new URL(download, window.location.origin);
  if (url.origin !== window.location.origin || url.pathname !== expectedPathname ||
    url.search !== '' || url.hash !== '' || url.username !== '' || url.password !== '') {
    throw new Error(UNAVAILABLE);
  }
  return url.href;
}

/** Read the same release feed as the desktop updater, so the download never pins an old version. */
export function readDesktopRelease(value: unknown): DesktopRelease {
  const release = value as { version?: unknown; downloads?: Record<string, unknown> } | null;
  const version = release?.version;
  if (typeof version !== 'string' || !/^\d+\.\d+\.\d+$/.test(version)) throw new Error(UNAVAILABLE);
  const feed = release?.downloads;
  const downloads: DesktopDownloadLink[] = [{
    platform: 'macos',
    url: readDownloadUrl(feed?.['darwin-aarch64'], `/desktop-updates/BigInt-Project_${version}_aarch64.dmg`),
  }];
  // Older feeds only publish macOS; a present-but-invalid Windows entry means the feed is wrong, so fail loudly.
  if (feed !== undefined && 'windows-x86_64' in feed) {
    downloads.push({
      platform: 'windows',
      url: readDownloadUrl(feed['windows-x86_64'], `/desktop-updates/BigInt-Project_${version}_x64-setup.exe`),
    });
  }
  return { version, downloads };
}

function currentPlatform(): DesktopPlatform {
  return /Windows/i.test(navigator.userAgent) ? 'windows' : 'macos';
}

export function DesktopDownload(): ReactElement {
  const [release, setRelease] = useState<DesktopRelease | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setFailed(false);
    void (async () => {
      try {
        const response = await fetch('/desktop-updates/latest.json', {
          cache: 'no-store', signal: controller.signal,
        });
        if (!response.ok) throw new Error('Release unavailable');
        const next = readDesktopRelease(await response.json());
        if (!controller.signal.aborted) setRelease(next);
      } catch {
        if (!controller.signal.aborted) setFailed(true);
      }
    })();
    return () => controller.abort();
  }, [attempt]);

  const preferred = currentPlatform();
  const downloads = release === null ? [] : [...release.downloads].sort(
    (a, b) => Number(b.platform === preferred) - Number(a.platform === preferred),
  );

  return (
    <section className="desktop-download" aria-label="桌面版下載">
      <div>
        <h2>BigInt Project 桌面版</h2>
        <p>離線管理專案，啟動時自動檢查更新。</p>
        <ul className="desktop-download-requirements muted">
          <li>macOS 12 以上 · Apple Silicon（M 系列晶片）</li>
          <li>Windows 10／11（64 位元）</li>
        </ul>
      </div>
      <div className="desktop-download-action" aria-live="polite">
        {failed ? (
          <>
            <span>暫時無法取得下載資訊</span>
            <button type="button" className="secondary" onClick={() => setAttempt((value) => value + 1)}>重試</button>
          </>
        ) : release === null ? (
          <span className="muted">正在取得最新版本…</span>
        ) : (
          <>
            <div className="desktop-download-buttons">
              {downloads.map((download, index) => (
                <a
                  key={download.platform}
                  className={index === 0 ? 'download-button' : 'download-button secondary'}
                  href={download.url}
                  download
                >
                  {PLATFORM_LABELS[download.platform].button}{' '}
                  <small>{PLATFORM_LABELS[download.platform].kind}</small>
                </a>
              ))}
            </div>
            <span className="muted">版本 {release.version}</span>
          </>
        )}
      </div>
    </section>
  );
}
